import { useList, useGo, useCustomMutation } from '@refinedev/core';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Plus, Eye, EyeOff, Trash2, BookOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable, type Column, type SortState, type BulkAction } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { PricingTiersList } from '@/components/shared/PricingTiersList';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import { getCourseTitle, type CourseListItem, type InstructorSelect } from './types';

// --- Утилиты ---

function getTranslatedName(
  translations: { languageCode: string; name: string }[] | undefined,
  fallback: string,
): string {
  if (!translations || translations.length === 0) {return fallback;}
  const ru = translations.find((t) => t.languageCode === 'ru');
  return ru?.name ?? translations[0]?.name ?? fallback;
}

// --- Константы ---

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;

const COURSE_TYPE_LABELS: Record<string, string> = {
  ONLINE: 'Онлайн',
  MEETING: 'Клуб',
  OFFLINE: 'Оффлайн',
  WEBINAR: 'Вебинар',
};

const COURSE_TYPE_COLORS: Record<string, string> = {
  ONLINE: 'bg-emerald-500/10 text-emerald-600',
  MEETING: 'bg-purple-500/10 text-purple-600',
  OFFLINE: 'bg-amber-500/10 text-amber-600',
  WEBINAR: 'bg-blue-500/10 text-blue-600',
};

// --- Вспомогательные функции ---

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface InstructorDisplay {
  id: string | number;
  name: string;
  photoUrl: string | null;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {return '?';}
  if (parts.length === 1) {return parts[0].slice(0, 2).toUpperCase();}
  const first = parts.at(0) ?? '';
  const last = parts.at(-1) ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

function getInstructorsDisplay(
  item: CourseListItem,
  instructorsMap: Map<string | number, InstructorSelect>,
): InstructorDisplay[] {
  const resolve = (instr: { id: string | number; name?: string; photoUrl: string | null; translations?: { languageCode: string; name: string }[] }): InstructorDisplay => {
    const fromMap = instructorsMap.get(instr.id);
    const source = fromMap ?? instr;
    const name = getTranslatedName(source.translations, source.name ?? '');
    return {
      id: source.id,
      name: name || '—',
      photoUrl: source.photoUrl ?? null,
    };
  };

  if (item.instructors && item.instructors.length > 0) {
    return item.instructors.map(resolve);
  }
  if (item.instructor) {
    return [resolve(item.instructor)];
  }
  return [];
}

// --- Хук дебаунса ---

function useDebouncedValue(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

// --- Фильтры ---

interface CourseFiltersProps {
  courseType: string;
  onCourseTypeChange: (value: string) => void;
  publishedStatus: string;
  onPublishedStatusChange: (value: string) => void;
  instructorId: string;
  onInstructorIdChange: (value: string) => void;
  instructors: InstructorSelect[];
}

function CourseFilters({
  courseType,
  onCourseTypeChange,
  publishedStatus,
  onPublishedStatusChange,
  instructorId,
  onInstructorIdChange,
  instructors,
}: CourseFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Тип курса */}
      <Select value={courseType} onValueChange={onCourseTypeChange}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Тип курса" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Все типы</SelectItem>
          <SelectItem value="ONLINE">Онлайн</SelectItem>
          <SelectItem value="MEETING">Клуб</SelectItem>
          <SelectItem value="OFFLINE">Оффлайн</SelectItem>
          <SelectItem value="WEBINAR">Вебинар</SelectItem>
        </SelectContent>
      </Select>

      {/* Статус публикации */}
      <Select value={publishedStatus} onValueChange={onPublishedStatusChange}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Статус" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Все статусы</SelectItem>
          <SelectItem value="true">Опубликован</SelectItem>
          <SelectItem value="false">Черновик</SelectItem>
        </SelectContent>
      </Select>

      {/* Преподаватель */}
      <Select value={instructorId} onValueChange={onInstructorIdChange}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Преподаватель" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Все преподаватели</SelectItem>
          {instructors.map((instr) => (
            <SelectItem key={instr.id} value={String(instr.id)}>
              {getTranslatedName(instr.translations, instr.name ?? `#${instr.id}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// --- Колонки ---

function useColumns(
  instructorsMap: Map<string | number, InstructorSelect>,
  onTierClick: (courseId: number, tierId: number) => void,
): Column<CourseListItem>[] {
  return useMemo(
    () => [
      {
        key: 'slug',
        header: 'Название',
        sortable: true,
        render: (_, record) => (
          <span className="font-medium">{getCourseTitle(record)}</span>
        ),
      },
      {
        key: 'courseType',
        header: 'Тип',
        sortable: true,
        width: '130px',
        render: (value) => {
          const type = String(value);
          return (
            <Badge
              variant="secondary"
              className={COURSE_TYPE_COLORS[type] ?? ''}
            >
              {COURSE_TYPE_LABELS[type] ?? type}
            </Badge>
          );
        },
      },
      {
        key: 'instructor',
        header: 'Преподаватели',
        render: (_, record) => {
          const list = getInstructorsDisplay(record, instructorsMap);
          if (list.length === 0) {
            return <span className="text-sm text-muted-foreground">{'\u2014'}</span>;
          }
          return (
            <div className="flex items-center -space-x-2">
              {list.map((instr) => (
                <Tooltip key={instr.id}>
                  <TooltipTrigger asChild>
                    <Avatar className="size-8 border-2 border-background hover:z-10 transition-transform hover:scale-110 cursor-default">
                      {instr.photoUrl ? (
                        <AvatarImage src={instr.photoUrl} alt={instr.name} />
                      ) : null}
                      <AvatarFallback className="text-xs">
                        {getInitials(instr.name)}
                      </AvatarFallback>
                    </Avatar>
                  </TooltipTrigger>
                  <TooltipContent>{instr.name}</TooltipContent>
                </Tooltip>
              ))}
            </div>
          );
        },
      },
      {
        key: 'isPublished',
        header: 'Статус',
        sortable: true,
        width: '180px',
        render: (_, record) => (
          <div className="flex items-center gap-1.5">
            {record.isPublished ? (
              <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
                Опубликован
              </Badge>
            ) : (
              <Badge variant="secondary">Черновик</Badge>
            )}
            {record.isPurchasable && (
              <Badge variant="outline" className="text-xs">
                Продаётся
              </Badge>
            )}
          </div>
        ),
      },
      {
        key: 'pricingTiers',
        header: 'Тарифы',
        render: (_, record) => (
          <PricingTiersList
            tiers={record.pricingTiers}
            onTierClick={(tier) => onTierClick(Number(record.id), tier.id)}
          />
        ),
      },
      {
        key: 'createdAt',
        header: 'Создан',
        sortable: true,
        width: '120px',
        render: (value) => (
          <span className="text-sm text-muted-foreground">
            {formatDate(String(value))}
          </span>
        ),
      },
    ],
    [instructorsMap, onTierClick],
  );
}

// --- Основной компонент ---

export function CoursesList() {
  useScrollRestoration();

  const go = useGo();

  // Пагинация и сортировка
  const [page, setPage] = usePersistedState('courses:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('courses:sort', {
    field: 'createdAt',
    order: 'desc',
  });

  // Поиск с дебаунсом
  const [searchInput, setSearchInput] = usePersistedState('courses:search', '');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  // Фильтры
  const [courseTypeFilter, setCourseTypeFilter] = usePersistedState('courses:courseType', 'all');
  const [publishedFilter, setPublishedFilter] = usePersistedState('courses:published', 'all');
  const [instructorFilter, setInstructorFilter] = usePersistedState('courses:instructor', 'all');

  // Сброс страницы при изменении фильтров/поиска
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, courseTypeFilter, publishedFilter, instructorFilter, setPage]);

  // Формирование фильтров для API
  const queryFilters = useMemo(() => {
    const filters: Array<{ field: string; operator: 'eq' | 'contains'; value: string }> = [];

    if (debouncedSearch) {
      filters.push({ field: 'search', operator: 'contains', value: debouncedSearch });
    }
    if (courseTypeFilter !== 'all') {
      filters.push({ field: 'courseType', operator: 'eq', value: courseTypeFilter });
    }
    if (publishedFilter !== 'all') {
      filters.push({ field: 'isPublished', operator: 'eq', value: publishedFilter });
    }
    if (instructorFilter !== 'all') {
      filters.push({ field: 'instructorId', operator: 'eq', value: instructorFilter });
    }

    return filters;
  }, [debouncedSearch, courseTypeFilter, publishedFilter, instructorFilter]);

  // Загрузка курсов
  const listResult = useList<CourseListItem>({
    resource: 'courses',
    pagination: { currentPage: page, pageSize: PAGE_SIZE, mode: 'server' },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result.data ?? [];
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;
  const refetch = listResult.query.refetch;

  // Загрузка преподавателей для фильтров
  const teachersResult = useList<InstructorSelect>({
    resource: 'teachers',
    pagination: { mode: 'off' },
  });
  const instructors = useMemo(
    () => teachersResult.result.data ?? [],
    [teachersResult.result.data],
  );

  const instructorsMap = useMemo(() => {
    const map = new Map<string | number, InstructorSelect>();
    for (const instr of instructors) {
      map.set(instr.id, instr);
    }
    return map;
  }, [instructors]);

  const handleTierClick = useCallback(
    (courseId: number, tierId: number) => {
      go({ to: `/courses/show/${courseId}`, query: { tab: 'pricing', tier: tierId } });
    },
    [go],
  );

  const columns = useColumns(instructorsMap, handleTierClick);

  // Bulk actions
  const bulkMutation = useCustomMutation();

  const handleBulkActivate = useCallback(
    (selectedRows: CourseListItem[]) => {
      const ids = selectedRows.map((r) => r.id);
      bulkMutation.mutate(
        { url: '/courses/bulk/activate', method: 'post', values: { ids } },
        { onSuccess: () => refetch() },
      );
    },
    [bulkMutation, refetch],
  );

  const handleBulkDeactivate = useCallback(
    (selectedRows: CourseListItem[]) => {
      const ids = selectedRows.map((r) => r.id);
      bulkMutation.mutate(
        { url: '/courses/bulk/deactivate', method: 'post', values: { ids } },
        { onSuccess: () => refetch() },
      );
    },
    [bulkMutation, refetch],
  );

  const handleBulkDelete = useCallback(
    (selectedRows: CourseListItem[]) => {
      const ids = selectedRows.map((r) => r.id);
      bulkMutation.mutate(
        { url: '/courses/bulk/delete', method: 'post', values: { ids } },
        { onSuccess: () => refetch() },
      );
    },
    [bulkMutation, refetch],
  );

  const bulkActions: BulkAction<CourseListItem>[] = useMemo(
    () => [
      {
        label: 'Опубликовать',
        icon: <Eye className="h-4 w-4" />,
        onClick: handleBulkActivate,
        showOnlyWhenSelected: true,
      },
      {
        label: 'Снять с публикации',
        icon: <EyeOff className="h-4 w-4" />,
        onClick: handleBulkDeactivate,
        showOnlyWhenSelected: true,
      },
      {
        label: 'Удалить',
        icon: <Trash2 className="h-4 w-4" />,
        onClick: handleBulkDelete,
        variant: 'destructive',
        showOnlyWhenSelected: true,
      },
    ],
    [handleBulkActivate, handleBulkDeactivate, handleBulkDelete],
  );

  // Обработчики фильтров с сбросом страницы
  const handleCourseTypeChange = useCallback((value: string) => {
    setCourseTypeFilter(value);
  }, [setCourseTypeFilter]);

  const handlePublishedChange = useCallback((value: string) => {
    setPublishedFilter(value);
  }, [setPublishedFilter]);

  const handleInstructorChange = useCallback((value: string) => {
    setInstructorFilter(value);
  }, [setInstructorFilter]);

  const handleRowClick = useCallback(
    (record: CourseListItem) => {
      go({ to: `/courses/show/${record.id}` });
    },
    [go],
  );

  const handleSearch = useCallback((value: string) => {
    setSearchInput(value);
  }, [setSearchInput]);

  return (
    <div className="space-y-6">
      <FormHeader
        title="Курсы"
        description="Управление курсами и учебными материалами"
        sticky
        icon={<BookOpen className="h-5 w-5" />}
        actions={
          <Button onClick={() => go({ to: '/courses/create' })}>
            <Plus className="h-4 w-4 mr-2" />
            Создать курс
          </Button>
        }
      />

      {/* Фильтры inline */}
      <CourseFilters
        courseType={courseTypeFilter}
        onCourseTypeChange={handleCourseTypeChange}
        publishedStatus={publishedFilter}
        onPublishedStatusChange={handlePublishedChange}
        instructorId={instructorFilter}
        onInstructorIdChange={handleInstructorChange}
        instructors={instructors}
      />

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        isLoading={isLoading}
        onPageChange={setPage}
        onSearch={handleSearch}
        searchValue={searchInput}
        searchPlaceholder="Поиск по названию курса..."
        onRowClick={handleRowClick}
        emptyMessage="Нет курсов"
        selectable
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />
    </div>
  );
}
